import { useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import CalendarClockIcon from '@liquidweb-dev/undercurrent/icons/lucide/CalendarClock';
import ArrowLeftRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeftRight';
import RefreshCwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RefreshCw';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import MailIcon from '@liquidweb-dev/undercurrent/icons/lucide/Mail';
import { fromApiBoolean, toApiBoolean } from '@liquidweb-dev/club-sauce';
import type { LWApiPublicDomainDetailsRawI } from '@liquidweb-dev/club-sauce/public/domain/index.raw';
import useDomainAutoRenew from 'modules/queries/network/dns/domain/useAutoRenew';
import useDomainServicesSummary from 'modules/queries/domain/services/useSummary';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import { Link } from 'react-router-dom';
import { useActionsContext } from './actionsContext';

type DomainItem = LWApiPublicDomainDetailsRawI;

interface ActionsMenuProps {
	item: DomainItem;
	cdnDomains: Record<string, string>;
	disabled?: boolean;
}

const ActionsMenu = (props: ActionsMenuProps) => {
	const { item, cdnDomains, disabled } = props;

	const wwwHostname = useSelector(appConfigSelectors.getWwwHostname);

	const { mutate: toggleAutoRenew, isLoading: isLoadingToggleAutoRenew } =
		useDomainAutoRenew();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const { toggleRenewDrawer, toggleTransferDialog } = useActionsContext();
	const open = Boolean(anchorEl);

	const { domain, dreg } = item;
	const canTransferOut = Boolean(dreg?.uniq_id);
	const isAutoRenewEnabled = fromApiBoolean(dreg?.autorenew);
	const isRenewAvailable = fromApiBoolean(dreg?.renewable);
	const isCloudflareActive = cdnDomains[domain] !== undefined;

	const { data: isBusinessEmailPurchased, isLoading: isLoadingDomainSummary } =
		useDomainServicesSummary(
			{ domain },
			{
				select: (data) => Boolean(data?.business_email),
			},
		);

	const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleRenewClick = () => {
		handleMenuClose();
		toggleRenewDrawer();
	};

	const handleTransferClick = () => {
		handleMenuClose();
		toggleTransferDialog();
	};

	const handleAutoRenewClick = () => {
		handleMenuClose();
		toggleAutoRenew({
			uniq_id: item.dreg?.uniq_id,
			allow: toApiBoolean(!isAutoRenewEnabled),
		});
	};

	const getAutoRenewText = () => {
		if (isAutoRenewEnabled) {
			return isLoadingToggleAutoRenew
				? 'Enabling auto renew'
				: 'Disable auto renew';
		}
		return isLoadingToggleAutoRenew
			? 'Disabling auto renew'
			: 'Enable auto renew';
	};

	return (
		<>
			<IconButton variant="ghost" onClick={handleMenuOpen} disabled={disabled}>
				<EllipsisIcon />
			</IconButton>
			<Menu
				open={open}
				onClose={handleMenuClose}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				{isRenewAvailable && (
					<>
						<MenuItem onClick={handleRenewClick}>
							<ListItemIcon>
								<CalendarClockIcon />
							</ListItemIcon>
							<ListItemText primary="Renew registration" />
						</MenuItem>

						<MenuItem
							onClick={handleAutoRenewClick}
							disabled={isLoadingToggleAutoRenew}
						>
							<ListItemIcon>
								<RefreshCwIcon />
							</ListItemIcon>
							<ListItemText primary={getAutoRenewText()} />
						</MenuItem>
					</>
				)}

				{canTransferOut && (
					<MenuItem onClick={handleTransferClick}>
						<ListItemIcon>
							<ArrowLeftRightIcon />
						</ListItemIcon>
						<ListItemText primary="Transfer domain" />
					</MenuItem>
				)}

				<MenuItem
					component={Link}
					to={
						isCloudflareActive
							? `/domain/cloudflare/${cdnDomains[domain]}`
							: '/domain/cloudflare'
					}
				>
					<ListItemIcon>
						<PlusIcon />
					</ListItemIcon>
					<ListItemText
						primary={
							isCloudflareActive ? 'Manage Cloudflare' : 'Add Cloudflare'
						}
					/>
				</MenuItem>
				{isBusinessEmailPurchased ? (
					<MenuItem
						component={Link}
						to="/services/premium-business-email"
						disabled={isLoadingDomainSummary}
					>
						<ListItemIcon>
							<MailIcon />
						</ListItemIcon>
						<ListItemText primary="Manage Business Email" />
					</MenuItem>
				) : (
					<MenuItem
						onClick={() => {
							handleMenuClose();
							window.open(
								`${wwwHostname}/premium-email-hosting`,
								'_blank',
								'noopener,noreferrer',
							);
						}}
						disabled={isLoadingDomainSummary}
					>
						<ListItemIcon>
							<MailIcon />
						</ListItemIcon>
						<ListItemText primary="Add Business Email" />
					</MenuItem>
				)}
			</Menu>
		</>
	);
};

export default ActionsMenu;
